import { useCallback, useEffect, useRef, useState } from "react";

export default function Slider() {
  const [sliders, setSliders] = useState([]);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(false);
  const currentYear = new Date().getFullYear();
  const timeoutRef = useRef(null);
  const touchStartX = useRef(null);
  const delay = 5000;

  const fetchSliders = async () => {
    try {
      const res = await fetch("https://code.gurusport.in/api/site-slider");

      const data = await res.json();

      if (data?.data?.length > 0) {
        const formatted = data.data.map((item) => ({
          ...item,
          imageUrl: item.background_image_url
            ? item.background_image_url
            : `https://code.gurusport.in/storage/${item.background_image}`,
        }));

        setSliders(formatted);
      } else {
        setSliders([]);
      }
    } catch (error) {
      console.error("Error fetching sliders:", error);
      setSliders([]);
    }
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  const nextSlide = useCallback(
    () =>
      setCurrent((prev) => (sliders.length ? (prev + 1) % sliders.length : 0)),
    [sliders.length]
  );

  const prevSlide = useCallback(
    () =>
      setCurrent((prev) =>
        sliders.length ? (prev - 1 + sliders.length) % sliders.length : 0
      ),
    [sliders.length]
  );

  useEffect(() => {
    if (!sliders.length) return;

    timeoutRef.current = setTimeout(nextSlide, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [current, sliders, nextSlide]);

  // Progress track: reset to 0, then let CSS run it to 100% over `delay`
  useEffect(() => {
    if (!sliders.length) return;

    setProgress(false);
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => setProgress(true))
    );

    return () => cancelAnimationFrame(frame);
  }, [current, sliders]);

  // Keyboard support
  useEffect(() => {
    if (!sliders.length) return;

    const onKey = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sliders.length, nextSlide, prevSlide]);

  // Swipe support for touch devices
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      diff < 0 ? nextSlide() : prevSlide();
    }
    touchStartX.current = null;
  };

  if (!sliders.length) return null;

  return (
    <section className="relative bg-white text-black overflow-hidden">
      {/* Hairline frame */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-black/10" />

      <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] lg:min-h-[700px]">
        {/* LEFT FIXED CONTENT */}
        <div className="relative flex items-center px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 pt-10 pb-8 sm:py-14 lg:py-16 bg-white z-20 order-2 lg:order-1">
          {/* Vertical brand rule (desktop) */}
          <div className="hidden lg:flex absolute left-6 top-16 bottom-16 w-px bg-black/10 items-center justify-center">
            <span className="absolute [writing-mode:vertical-rl] rotate-180 text-[10px] tracking-[0.4em] text-black/35 bg-white py-4">
              gurusport
            </span>
          </div>

          <div className="w-full">
            <div className="flex items-center gap-3 mb-6 sm:mb-9">
              <span className="w-2.5 h-2.5 bg-red-600" />
              <span className="text-sm text-black/60">
                New for {currentYear}
              </span>
            </div>

            <h1 className="font-black leading-[0.88] uppercase">
              <span className="block text-black text-[46px] sm:text-[64px] md:text-[92px] xl:text-[118px]">
                Outwork
              </span>

              <span
                className="block text-transparent text-[46px] sm:text-[64px] md:text-[92px] xl:text-[118px] [-webkit-text-stroke:1.5px_#000] md:[-webkit-text-stroke:2px_#000]"
              >
                Outlast
              </span>

              <span className="block text-red-600 text-[46px] sm:text-[64px] md:text-[92px] xl:text-[118px]">
                Outplay
              </span>
            </h1>

            <p className="mt-7 sm:mt-10 max-w-sm text-gray-600 text-sm sm:text-base leading-7 sm:leading-8">
              Built for the hours nobody sees. Breathable weaves, reinforced
              seams and a cut that stays put from the first whistle to the
              last.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-5">
              <a
                href="/collections"
                className="inline-flex items-center bg-black text-white text-sm font-semibold tracking-wide px-7 py-3.5 hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
              >
                Shop the collection
              </a>

              <a
                href="/team-kit"
                className="text-sm font-semibold border-b-2 border-red-600 pb-1 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
              >
                Build a team kit
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT SLIDER */}
        <div
          className="relative w-full overflow-hidden bg-white order-1 lg:order-2 lg:min-h-[700px]"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Diagonal red seam along the split */}
          <div className="hidden lg:block absolute -left-24 top-0 bottom-0 w-24 bg-red-600 skew-x-[-8deg] opacity-90 z-20" />

          {/* Faint year mark, clipped bottom-right */}
          <div className="hidden lg:block absolute -bottom-16 right-6 pointer-events-none select-none">
            <span className="text-[16rem] font-black text-black/[0.04] leading-none">
              {currentYear}
            </span>
          </div>

          {/*
            Sizer image: not absolutely positioned, so it gives the container
            its natural height on mobile/tablet instead of a fixed 700px box.
            Hidden from view and from screen readers.
          */}
          <img
            src={sliders[0].imageUrl}
            alt=""
            aria-hidden="true"
            className="block lg:hidden w-full h-auto invisible"
          />

          {sliders.map((slider, index) => (
            <a
              key={slider.id}
              href={slider.target_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className={`absolute inset-0 transition-opacity duration-1000 ${
                current === index
                  ? "opacity-100 z-10"
                  : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              {/* Red glow, weighted to the lower left */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_75%,rgba(220,38,38,0.16),transparent_62%)]" />

              {/* Main hero image */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={slider.imageUrl}
                  alt={slider.title || ""}
                  className={`w-full h-full lg:w-[94%] lg:h-[94%] object-contain transition-transform duration-[6000ms] ease-linear motion-reduce:transition-none ${
                    current === index ? "scale-100" : "scale-105"
                  }`}
                />
              </div>
            </a>
          ))}

          {/* Control cluster: arrows + progress track */}
          <div className="absolute bottom-3 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 z-30 flex items-center gap-4 sm:gap-6">
            <div className="flex">
              <button
                onClick={prevSlide}
                aria-label="Previous slide"
                className="w-9 h-9 sm:w-11 sm:h-11 border border-black/15 bg-white/85 backdrop-blur-sm text-black text-sm flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 transition-colors"
              >
                ❮
              </button>

              <button
                onClick={nextSlide}
                aria-label="Next slide"
                className="w-9 h-9 sm:w-11 sm:h-11 border border-l-0 border-black/15 bg-white/85 backdrop-blur-sm text-black text-sm flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 transition-colors"
              >
                ❯
              </button>
            </div>

            <div className="flex flex-1 items-center gap-1.5 sm:gap-2">
              {sliders.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrent(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={current === index}
                  className="relative h-4 flex-1 flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                >
                  <span className="block w-full h-[3px] bg-black/15">
                    <span
                      className="block h-full bg-red-600 ease-linear"
                      style={{
                        width:
                          index < current
                            ? "100%"
                            : index === current
                            ? progress
                              ? "100%"
                              : "0%"
                            : "0%",
                        transitionProperty: "width",
                        transitionDuration:
                          index === current ? `${delay}ms` : "0ms",
                      }}
                    />
                  </span>
                </button>
              ))}
            </div>

            <span className="hidden sm:block text-xs font-semibold tabular-nums text-black/50">
              {String(current + 1).padStart(2, "0")}
              <span className="text-black/25"> / </span>
              {String(sliders.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}